import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { DataLakeEnums } from 'dashboard-services/api/dataLake'
import Query from 'dashboard-services/api/Query'
import { globalActions } from 'primary-components'

import getApiConfig from 'api/ApiConfig'
import { filesDatalakeQueries } from 'queries/modules/datalake'

import groupConfigService from '../groupConfigService'

export default (() => {
  const { EMPTY_DYNAMIC_VALIDATION, EMPTY_GROUP_CONFIG } = groupConfigService
  const LOAD_ERROR_MESSAGE = "Group configuration could not be loaded."

  const useLoadConfigFile = ({ groupName, fileName, canLoad, enabled, emptyValue, parse = undefined }) => {
    const dispatch = useDispatch(),
          [value, setValue] = useState(canLoad ? undefined : emptyValue),
          [loadedValue, setLoadedValue] = useState(canLoad ? undefined : emptyValue),
          hasLoaded = useRef(false),
          isMounted = useRef(true),
          getQueryBuilder = useCallback(() =>
            new Query.Builder(dispatch(getApiConfig()))
              .withShouldDisplayError(false)
          , [dispatch]),
          onFileNotFound = useCallback(() => {}, []),
          getFile = filesDatalakeQueries.useReturnFileContent({ getQueryBuilder, fileName, groupName, extraQuery: `type=${DataLakeEnums.FILE_TYPE.CONFIG}`, onFileNotFound }),
          setLoaded = useCallback(loaded => {
            setValue(loaded)
            setLoadedValue(loaded)
          }, [])

    useEffect(() => {
      isMounted.current = true
      return () => {
        isMounted.current = false
      }
    }, [])

    useEffect(() => {
      if(!canLoad || !enabled || hasLoaded.current) {
        return
      }
      hasLoaded.current = true
      getFile()
        .then(r => r?.content ? r.content.json().then(json => setLoaded(parse ? parse(json) : json)) : setLoaded(emptyValue))
        .catch(() => isMounted.current && dispatch(globalActions.getMessageStore()).error(LOAD_ERROR_MESSAGE))
    }, [canLoad, dispatch, emptyValue, enabled, getFile, parse, setLoaded])

    const toReturn = useMemo(() => ({ value, setValue, loadedValue }), [loadedValue, value])

    return toReturn
  }

  const useGroupConfigFiles = ({ isCreate, groupName, enabled }) => {
    const canLoad = !isCreate && !!groupName,
          { value: groupConfig, setValue: setGroupConfig, loadedValue: loadedGroupConfig } = useLoadConfigFile({
            groupName,
            fileName: `.${groupName}.json`,
            canLoad,
            enabled,
            emptyValue: EMPTY_GROUP_CONFIG,
            parse: groupConfigService.normalizeGroupConfig
          }),
          { value: dynamicValidation, setValue: setDynamicValidation, loadedValue: dynamicValidationFileContent } = useLoadConfigFile({
            groupName,
            fileName: `.${groupName}_ncsv_dynamic_validation.json`,
            canLoad,
            enabled,
            emptyValue: EMPTY_DYNAMIC_VALIDATION
          }),
          isLoaded = !!groupConfig && !!dynamicValidation

    return useMemo(() => ({
      isLoaded,
      groupConfig,
      setGroupConfig,
      loadedGroupConfig,
      dynamicValidation,
      setDynamicValidation,
      dynamicValidationFileContent
    }), [dynamicValidation, dynamicValidationFileContent, groupConfig, isLoaded, loadedGroupConfig, setDynamicValidation, setGroupConfig])
  }

  return {
    useGroupConfigFiles
  }
})()
